import { CopyButton } from "@/components/ui/copy-button";
import { Input } from "@/components/ui/input";

/**
 * The "Type <phrase> to confirm" gate of a destructive dialog: the phrase
 * (selectable, with a copy button) over a destructive-focused input. The
 * caller owns the value and enables its button when it equals `phrase`.
 *
 * @example
 * // Inside an AlertDialog; see alert-dialog for the whole dialog.
 * const [typed, setTyped] = useState("")
 * const confirmed = typed === organization.name
 *
 * <TypeToConfirm
 *   phrase={organization.name}
 *   value={typed}
 *   onValueChange={setTyped}
 *   onEnter={() => confirmed && deleteOrganization()}
 * />
 * <AlertDialogAction variant="destructive" disabled={!confirmed} onClick={deleteOrganization}>
 *   Delete organization
 * </AlertDialogAction>
 */
export function TypeToConfirm({
  phrase,
  value,
  onValueChange,
  onEnter,
  disabled,
}: {
  phrase: string;
  value: string;
  onValueChange: (value: string) => void;
  /** Enter in the input — usually the dialog's action, when the phrase matches. */
  onEnter?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1 text-sm">
        <span>Type</span>
        <span className="flex items-center">
          <span className="select-all rounded-l-sm bg-muted-foreground/10 p-0.5 pl-1.75 font-semibold">
            {phrase}
          </span>
          <CopyButton
            buttonText=""
            className="size-6 rounded-l-none rounded-r-sm border-none bg-muted-foreground/10 hover:bg-muted-foreground/20 [&_svg]:size-3.5"
            tooltipText="Copy"
            textToCopy={phrase}
            variant="secondary"
            tooltipDelay={500}
            scope={false}
          />
        </span>
        <span>to confirm.</span>
      </div>
      <Input
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && value === phrase) onEnter?.();
        }}
        placeholder={phrase}
        autoComplete="off"
        disabled={disabled}
        className="focus-visible:border-destructive focus-visible:ring-destructive/20"
      />
    </div>
  );
}
